import { useLocation, useNavigate } from "react-router-dom";
import { EDIT_PARAM } from "@/profitability/routes";

/**
 * What an Edit agent opened from elsewhere carries in the address's state:
 * a message to send as the conversation loads (`request`, from Settings →
 * Ask for a check), or words to leave in its composer for the owner to
 * finish (`draft`, from an empty state's suggestion).
 */
export interface RuleEditContext {
  id: string;
  label: string;
  href: string;
  example?: { name: string; flag: string; dataset: string; recordId: string };
}

export interface EditAgentState {
  ruleContext?: RuleEditContext;
  request?: string;
  draft?: string;
}

/**
 * Edit agent (the agent's conversation) beside its page, held in the
 * address so a refresh or a shared link keeps it: `?edit=1` open, `?edit=0`
 * closed. Without either it opens while the agent is being built, the one
 * thing to do then, and stays closed once the agent runs.
 */
export const useAgentNav = (settingUp: boolean) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const param = new URLSearchParams(search).get(EDIT_PARAM);
  const query = (value: "1" | "0" | null) => {
    const next = new URLSearchParams(search);
    if (value) next.set(EDIT_PARAM, value);
    else next.delete(EDIT_PARAM);
    const text = next.toString();
    return text ? `?${text}` : "";
  };
  return {
    editing: param === "1" || (param === null && settingUp),
    /** Whether the address says so, rather than the agent's state. */
    explicit: param !== null,
    /** Opens Edit agent beside the page, `draft` waiting in its composer. */
    edit: (draft?: string) =>
      navigate(`${pathname}${query("1")}`, {
        state: draft ? ({ draft } satisfies EditAgentState) : null,
      }),
    closeEdit: () =>
      navigate(`${pathname}${query(settingUp ? "0" : null)}`, {
        replace: true,
      }),
  };
};
